/**
 * @fileoverview Checklist Summary Component for Step 2
 * @module ChecklistSummary
 * 
 * Business Context:
 * - Displays a comprehensive summary of all deferred maintenance findings
 * - Shows total issues, estimated costs, and severity breakdown
 * - Celebrates module completion and prompts continuation to Step 3
 * 
 * Technical Context:
 * - Receives report data with all category findings
 * - Calculates aggregate statistics across all categories
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  Layers,
  Wind,
  Droplets,
  Zap,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Trophy,
  FileText,
  DollarSign,
  Sparkles
} from 'lucide-react';

interface Finding {
  id: string;
  category: string;
  checklistData: Record<string, boolean>;
  followUpData?: Record<string, Record<string, string | number>>;
  estimatedCost: number;
  severity: string;
  notes?: string;
}

interface ChecklistSummaryProps {
  findings: Finding[];
  onBack: () => void;
  onNavigateToStep3?: () => void;
  /** Whether the user has received their Notice of Appraised Value (gates Step 2) */
  hasNoticeReceived?: boolean;
}

// Question labels for each category - must match IDs from individual checklist components
const questionLabels: Record<string, Record<string, string>> = {
  ROOF: {
    age_over_15: 'Roof Over 15 Years Old',
    visible_damage: 'Visible Damaged, Curling, or Missing Shingles',
    hail_damage: 'Evidence of Hail Damage',
    leaks_stains: 'Water Stains on Ceilings or Walls',
    granule_loss: 'Granules in Gutters or Downspouts',
    sagging_areas: 'Visible Sagging in Roof Deck',
    flashing_damage: 'Damaged Flashing Around Vents/Chimneys',
    attic_ventilation: 'Inadequate Attic Ventilation',
    wind_damage: 'Evidence of Wind Damage from Storms',
    professional_opinion: 'Professional Recommended Repairs',
  },
  FOUNDATION: {
    visible_cracks: 'Visible Cracks in Foundation or Slab',
    stair_step_cracks: 'Stair-Step Cracks in Brick Veneer',
    horizontal_cracks: 'Horizontal Cracks in Foundation',
    doors_windows_stick: 'Doors or Windows Stick or Won\'t Close',
    uneven_floors: 'Uneven or Sloping Floors',
    gaps_walls_ceiling: 'Gaps Between Walls and Ceiling/Floor',
    soil_separation: 'Visible Separation Between Soil and Foundation',
    pier_beam_issues: 'Sagging Floors or Damaged Piers (Pier & Beam)',
    previous_repairs: 'Signs of Previous Foundation Repairs',
    professional_inspection: 'Structural Engineer Recommended Repairs',
  },
  HVAC: {
    age_over_10: 'HVAC System Over 10 Years Old',
    uneven_cooling: 'Rooms Don\'t Cool Properly in Summer',
    high_utility_bills: 'Significantly Increased Electric Bills',
    struggles_over_100: 'AC Struggles When Temperature Exceeds 100°F',
    strange_noises: 'System Makes Unusual Noises',
    poor_airflow: 'Weak Airflow From Vents',
    frequent_cycling: 'System Turns On/Off Frequently',
    humidity_issues: 'Humidity Problems in Home',
    ductwork_issues: 'Damaged or Disconnected Attic Ductwork',
    professional_opinion: 'HVAC Professional Recommended Repairs',
  },
  PLUMBING: {
    pipe_age: 'Pipes Original or Over 30 Years Old',
    low_water_pressure: 'Low Water Pressure Throughout Home',
    slow_drains: 'Slow Drains in Multiple Fixtures',
    water_discoloration: 'Discolored Water from Faucets',
    visible_leaks: 'Visible Leaks Under Sinks or Around Fixtures',
    water_heater_issues: 'Water Heater Over 8-10 Years Old or Issues',
    slab_leak: 'Evidence of Slab Leak',
    sewer_issues: 'Sewer Odors or Backup Issues',
    polybutylene_pipes: 'Polybutylene (Gray) Pipes Present',
    freeze_damage: 'Pipe Damage from Freeze Event',
    professional_opinion: 'Plumber Recommended Repairs or Repiping',
  },
  ELECTRICAL: {
    panel_age: 'Electrical Panel Over 25 Years Old',
    fuse_box: 'Fuse Box Instead of Circuit Breakers',
    panel_brand: 'Federal Pacific, Zinsco, or Pushmatic Panel',
    frequent_trips: 'Circuit Breakers Trip Frequently',
    flickering_lights: 'Flickering or Dimming Lights When AC Runs',
    two_prong_outlets: 'Ungrounded (2-Prong) Outlets Present',
    warm_outlets: 'Outlets or Switches Feel Warm',
    aluminum_wiring: 'Aluminum Wiring Present',
    insufficient_capacity: 'Undersized Service (100 Amp or Less)',
    professional_opinion: 'Electrician Recommended Panel Upgrade or Rewiring',
  },
};

const categoryConfig: Record<string, { icon: React.ReactNode; label: string; color: string; bgColor: string }> = {
  ROOF: { 
    icon: <Home className="w-5 h-5" />, 
    label: 'Roof', 
    color: 'text-rose-600', 
    bgColor: 'bg-rose-100' 
  },
  FOUNDATION: { 
    icon: <Layers className="w-5 h-5" />, 
    label: 'Foundation', 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-100' 
  },
  HVAC: { 
    icon: <Wind className="w-5 h-5" />, 
    label: 'HVAC', 
    color: 'text-sky-600', 
    bgColor: 'bg-sky-100' 
  },
  PLUMBING: { 
    icon: <Droplets className="w-5 h-5" />, 
    label: 'Plumbing', 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-100' 
  },
  ELECTRICAL: { 
    icon: <Zap className="w-5 h-5" />, 
    label: 'Electrical', 
    color: 'text-yellow-600', 
    bgColor: 'bg-yellow-100' 
  },
};

const severityConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  low: { label: 'Low', color: 'text-emerald-700', bgColor: 'bg-emerald-100' },
  medium: { label: 'Medium', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  high: { label: 'High', color: 'text-orange-700', bgColor: 'bg-orange-100' },
  critical: { label: 'Critical', color: 'text-rose-700', bgColor: 'bg-rose-100' },
};

const ChecklistSummary: React.FC<ChecklistSummaryProps> = ({ findings, onBack, onNavigateToStep3, hasNoticeReceived = false }) => {
  const navigate = useNavigate();
  
  // Navigate to Step 2 if notice received, otherwise back to dashboard
  const handleNavigateForward = () => {
    if (hasNoticeReceived) {
      navigate('/step/2/file-appeal');
    } else if (onNavigateToStep3) {
      onNavigateToStep3();
    } else {
      navigate('/dashboard');
    }
  };
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Calculate totals
  const totalIssues = findings.reduce((sum, finding) => {
    const issues = Object.values(finding.checklistData || {}).filter(v => v === true).length;
    return sum + issues;
  }, 0);

  const totalCost = findings.reduce((sum, finding) => {
    return sum + (finding.estimatedCost || 0);
  }, 0);

  // Determine overall severity
  const getOverallSeverity = (): string => {
    const severities = findings.map(f => f.severity);
    if (severities.includes('critical')) return 'critical';
    if (severities.includes('high')) return 'high';
    if (severities.includes('medium')) return 'medium';
    return 'low';
  };

  const overallSeverity = getOverallSeverity();
  const severityStyle = severityConfig[overallSeverity] || severityConfig.low;

  // Format cost for display
  const formatCost = (cents: number): string => {
    if (cents === 0) return '$0';
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Get issues list for a category
  const getIssuesForCategory = (finding: Finding): string[] => {
    const labels = questionLabels[finding.category] || {};
    const issues: string[] = [];
    
    Object.entries(finding.checklistData || {}).forEach(([key, value]) => {
      if (value === true) {
        issues.push(labels[key] || key);
      }
    });
    
    return issues;
  };

  return (
    <div className="space-y-6">
      {/* Module Complete Celebration */}
      <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl p-8 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 w-32 h-32 border-4 border-white rounded-full" />
          <div className="absolute bottom-4 left-4 w-24 h-24 border-4 border-white rounded-full" />
          <Sparkles className="absolute top-8 left-8 w-8 h-8" />
          <Sparkles className="absolute bottom-8 right-8 w-6 h-6" />
        </div>
        
        <div className="relative">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Trophy className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Step 1 Complete!</h2>
              <p className="text-white/90">You've documented all deferred maintenance</p>
            </div>
          </div>
          
          <p className="text-white/80">
            Great work! Your documented issues will be used to build a compelling case for your property tax appeal. 
            The more thoroughly you document, the stronger your argument will be.
          </p>
          
          {!hasNoticeReceived && (
            <div className="mt-4 bg-white/15 backdrop-blur-sm rounded-lg px-4 py-3 text-white/90 text-sm">
              <p className="font-semibold mb-1">Waiting for your Notice of Appraised Value</p>
              <p className="text-white/70">Step 2 (File Your Appeal) unlocks once you receive your notice from the county. Head to your dashboard to mark it as received.</p>
            </div>
          )}
        </div>
      </div>

      {/* Documentation Summary Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-xl">
              <FileText className="w-6 h-6 text-slate-700" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">Documentation Summary</h3>
              <p className="text-slate-500 text-sm">Review your documented issues before generating arguments</p>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
          <div className="p-6 text-center">
            <div className="text-3xl font-bold text-slate-900">{totalIssues}</div>
            <div className="text-sm text-slate-500 mt-1">Issues Documented</div>
          </div>
          <div className="p-6 text-center">
            <div className="text-3xl font-bold text-emerald-600">{formatCost(totalCost)}</div>
            <div className="text-sm text-slate-500 mt-1">Estimated Repair Cost</div>
          </div>
          <div className="p-6 text-center">
            <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold ${severityStyle.bgColor} ${severityStyle.color}`}>
              {severityStyle.label} Severity
            </span>
            <div className="text-sm text-slate-500 mt-2">Overall Impact</div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="p-6">
          <h4 className="font-bold text-slate-900 mb-4">By Category</h4>
          
          <div className="space-y-3">
            {findings.map((finding) => {
              const config = categoryConfig[finding.category];
              const issues = getIssuesForCategory(finding);
              const issueCount = issues.length;
              const isExpanded = expandedCategories.has(finding.category);
              const findingSeverity = severityConfig[finding.severity] || severityConfig.low;

              if (!config) return null;

              return (
                <div 
                  key={finding.category}
                  className="border border-slate-200 rounded-xl overflow-hidden"
                >
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(finding.category)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${config.bgColor} rounded-lg flex items-center justify-center ${config.color}`}>
                        {config.icon}
                      </div>
                      <div className="text-left">
                        <h5 className="font-semibold text-slate-900">{config.label}</h5>
                        <p className="text-sm text-slate-500">{issueCount} issue{issueCount !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-semibold text-slate-900">{formatCost(finding.estimatedCost)}</div>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${findingSeverity.bgColor} ${findingSeverity.color}`}>
                          {findingSeverity.label.toLowerCase()}
                        </span>
                      </div>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </button>

                  {/* Expanded Issues List */}
                  {isExpanded && issues.length > 0 && (
                    <div className="px-4 pb-4 border-t border-slate-100">
                      <div className="pt-3 space-y-2">
                        {issues.map((issue, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-700">{issue}</span>
                          </div>
                        ))}
                        
                        {finding.notes && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                            <p className="text-xs text-slate-500 font-medium mb-1">Notes:</p>
                            <p className="text-sm text-slate-700">{finding.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* No issues message */}
                  {isExpanded && issues.length === 0 && (
                    <div className="px-4 pb-4 border-t border-slate-100">
                      <p className="pt-3 text-sm text-slate-500 italic">No issues documented for this category</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-slate-100 bg-slate-50 flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 py-3 px-6 bg-white border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
          >
            Edit Checklists
          </button>
          <button
            onClick={handleNavigateForward}
            className="flex-1 py-3 px-6 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
          >
            {hasNoticeReceived ? 'Continue to Step 2: File Your Appeal' : 'Back to Dashboard'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistSummary;

